import {
  CRM_APP_TYPE,
  THIRD_PARTY_CUSTOM_APP_TYPE
} from '../constants/constants'

/**
 * Every destination on the app detail page. These are flat — the old two-level
 * tab + segment split forced a second row of navigation chrome and hid half the
 * destinations behind a tab click. One sidebar shows all of them at once.
 */
export const APP_DETAIL_SECTIONS = [
  'overview',
  'people',
  'groups',
  'roles',
  'oidc',
  'scim',
  'credentials',
  'webhooks',
  'outboundWebhooks',
  'activity',
  'settings'
] as const

export type AppDetailSection = (typeof APP_DETAIL_SECTIONS)[number]

/** Sidebar groups. `null` heading = ungrouped item at the top level. */
export type AppDetailGroup = {
  key: string
  /** i18n key suffix under `workspace.apps.navGroups`, or null when ungrouped. */
  heading: string | null
  items: AppDetailSection[]
}

type SectionContext = {
  isThirdParty: boolean
  webhooksEnabled: boolean
  /** Outbound webhooks are CRM events, so the section only exists on a CRM app. */
  isCrm: boolean
}

export const navGroupsFor = ({
  isThirdParty,
  webhooksEnabled,
  isCrm
}: SectionContext): AppDetailGroup[] => [
  { key: 'top', heading: null, items: ['overview'] },
  { key: 'access', heading: 'access', items: ['people', 'groups', 'roles'] },
  {
    key: 'connection',
    heading: 'connection',
    items: [
      ...(isThirdParty ? (['oidc', 'scim'] as const) : []),
      'credentials',
      ...(webhooksEnabled ? (['webhooks'] as const) : []),
      ...(isCrm ? (['outboundWebhooks'] as const) : [])
    ]
  },
  { key: 'audit', heading: 'monitor', items: ['activity', 'settings'] }
]

export const availableSections = (ctx: SectionContext): AppDetailSection[] =>
  navGroupsFor(ctx).flatMap((group) => group.items)

export const isAppDetailSection = (
  value: string | null
): value is AppDetailSection =>
  value != null && APP_DETAIL_SECTIONS.includes(value as AppDetailSection)

/**
 * Pre-restructure `?tab=` values, kept so bookmarks and shared links keep
 * resolving. `users` became `people`; `audit` became `activity`.
 */
const LEGACY_SECTION_MAP: Record<string, AppDetailSection> = {
  users: 'people',
  roles: 'roles',
  oidc: 'oidc',
  scim: 'scim',
  credentials: 'credentials',
  webhooks: 'webhooks',
  audit: 'activity',
  access: 'people',
  connection: 'credentials'
}

export const resolveLegacySection = (
  value: string | null
): AppDetailSection | undefined =>
  value == null ? undefined : LEGACY_SECTION_MAP[value]

export const isThirdPartyApp = (instanceTypeCode: string | undefined) =>
  instanceTypeCode === THIRD_PARTY_CUSTOM_APP_TYPE

/**
 * Hiding the section on a non-CRM app is presentation only — the backend answers 404 for a
 * non-CRM application id, so a missed check here shows a broken menu entry rather than letting
 * one app's settings configure another's webhooks.
 */
export const isCrmApp = (instanceTypeCode: string | undefined) =>
  instanceTypeCode?.toLowerCase() === CRM_APP_TYPE
